import { generateSHA256Hash, validateFileType, validateFileSize, formatFileSize, calculateFileHash, validateMediaFile, extractMediaDuration, extractMediaDimensions } from '@/lib/utils'
import { validateFileTypeAndExtension, isSupportedMediaFile } from '@/lib/media-utils'
import { contentApi } from './api'
import { ContentItem, ContentMetadata, UploadProgress } from '@/types'
import { compressionService, CompressionResult, CompressionProgress } from './compressionService'

export interface UploadOptions {
  title?: string
  description?: string
  tags: string[]
  category?: string
  isPublic: boolean
  uploaderAddress?: string // For wallet integration
}

export interface ProcessingResult {
  cosmkId: string
  watermarkedFile?: File
  ipfsHash?: string
  metadata: ContentMetadata
  compressionResult?: CompressionResult
}

class UploadService {
  private readonly MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB
  private readonly SKIP_COMPRESSION = import.meta.env.VITE_SKIP_COMPRESSION === 'true' // Skip compression if enabled
  private readonly ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'video/mp4',
    'video/avi',
    'video/mov',
    'video/wmv',
    'video/x-matroska', // MKV support
    'audio/mpeg',
    'audio/wav',
    'audio/flac',
    'audio/aac',
    'audio/x-m4a',
    'audio/m4a'
  ]



  /**
   * Validate and process uploaded file with compression
   */
  async processFile(
    file: File, 
    options: UploadOptions,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<ProcessingResult> {
    // Validate file
    this.validateFile(file)

    let processedFile = file
    let compressionResult: CompressionResult | undefined

    // Step 1: Compress file if it's a supported media type
    if (!this.SKIP_COMPRESSION && compressionService.shouldCompress(file)) {
      // Check compression service availability first
      const compressionAvailability = await compressionService.isCompressionAvailable()
      
      if (compressionAvailability.available) {
        try {
          console.log(`🗜️ Starting compression for ${file.name} (${formatFileSize(file.size)})`)
          
          // Show warning if service is slow
          let initialMessage = 'Compressing file to reduce storage costs...'
          if (compressionAvailability.reason) {
            initialMessage += ` (${compressionAvailability.reason})`
          }
          
          onProgress?.({
            stage: 'compressing',
            progress: 0,
            message: initialMessage
          })

          // Add timeout for compression (15 minutes for external service)
          let compressionJobId: string | null = null
          
          const compressionPromise = compressionService.compressFile(
            file, 
            { quality: 'MEDIUM', preserveMetadata: true },
            (compressionProgress: CompressionProgress) => {
              // Track the job ID from progress updates
              if (compressionProgress.jobId) {
                compressionJobId = compressionProgress.jobId
              }
              
              onProgress?.({
                stage: 'compressing',
                progress: compressionProgress.progress * 0.3, // 30% of total progress
                message: compressionProgress.message
              })
            }
          )
          
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              // Cancel the compression job when timeout occurs
              if (compressionJobId) {
                compressionService.cancelJob(compressionJobId)
              }
              reject(new Error('Compression timeout after 15 minutes'))
            }, 15 * 60 * 1000)
          })
          
          compressionResult = await Promise.race([compressionPromise, timeoutPromise]) as any
          
          processedFile = compressionResult.compressedFile
          
          console.log(`✅ Compression completed! Saved ${compressionResult.compressionRatio.toFixed(1)}% - ` +
                     `${formatFileSize(compressionResult.originalSize)} → ` +
                     `${formatFileSize(compressionResult.compressedSize)}`)
                     
          onProgress?.({
            stage: 'compressing',
            progress: 30,
            message: `Compression saved ${compressionResult.compressionRatio.toFixed(1)}% file size`
          })

        } catch (error) {
          console.warn('⚠️ Compression failed, continuing with original file:', error)
          
          // Provide specific error message to user
          const errorMessage = error instanceof Error 
            ? error.message 
            : 'Compression service unavailable'
            
          onProgress?.({
            stage: 'processing',
            progress: 30,
            message: `Compression failed (${errorMessage}), using original file...`
          })
          // Continue with original file
        }
      } else {
        console.log(`⚠️ Compression service unavailable: ${compressionAvailability.reason}`)
        onProgress?.({
          stage: 'processing',
          progress: 30,
          message: `Compression unavailable (${compressionAvailability.reason}), using original file...`
        })
      }
    } else {
      if (this.SKIP_COMPRESSION) {
        console.log(`⏭️ Compression disabled by configuration`)
        onProgress?.({
          stage: 'processing',
          progress: 30,
          message: 'Compression disabled, proceeding with original file...'
        })
      } else {
        console.log(`⏭️ Skipping compression for ${file.name} (not suitable for compression)`)
        onProgress?.({
          stage: 'processing',
          progress: 30,
          message: 'File not suitable for compression, proceeding...'
        })
      }
    }

    // Step 2: Generate SHA-256 hash (COSMK ID) for DRM protection
    onProgress?.({
      stage: 'processing',
      progress: 40,
      message: 'Generating content fingerprint...'
    })
    
    const cosmkId = await calculateFileHash(processedFile)

    // Step 3: Create metadata
    const metadata: ContentMetadata = {
      title: options.title || file.name,
      description: options.description || '',
      tags: options.tags,
      category: options.category,
      size: processedFile.size,
      mimeType: processedFile.type,
      fingerprint: cosmkId,
      duration: undefined, // Will be extracted for media files
      dimensions: undefined, // Will be extracted for images/videos
      watermarkData: undefined, // Will be added after watermarking
      isPublic: options.isPublic
    }

    // Step 4: Extract media metadata
    onProgress?.({
      stage: 'processing',
      progress: 50,
      message: 'Extracting media metadata...'
    })
    
    await this.extractMediaMetadata(processedFile, metadata)

    // Step 5: Apply steganographic watermarking
    onProgress?.({
      stage: 'watermarking',
      progress: 60,
      message: 'Applying DRM watermarking...'
    })
    
    const watermarkedFile = await this.applyWatermarking(processedFile, cosmkId)

    // Step 6: Upload to IPFS
    onProgress?.({
      stage: 'uploading',
      progress: 80,
      message: 'Uploading to decentralized storage...'
    })
    
    const ipfsHash = await this.uploadToIPFS(watermarkedFile || processedFile)

    onProgress?.({
      stage: 'uploading',
      progress: 100,
      message: 'Upload completed successfully!'
    })

    return {
      cosmkId,
      watermarkedFile: watermarkedFile || undefined,
      ipfsHash,
      metadata,
      compressionResult
    }
  }

  /**
   * Validate file type and size with enhanced DRM validation
   */
  private validateFile(file: File): void {
    // Enhanced file type validation using both MIME type and extension
    if (!isSupportedMediaFile(file)) {
      const typeValidation = validateFileTypeAndExtension(file)
      let errorMessage = `File format not supported. MIME: ${file.type}, Extension: .${file.name.split('.').pop()}`
      if (typeValidation.suggestedCorrection) {
        errorMessage += `. ${typeValidation.suggestedCorrection}`
      }
      throw new Error(errorMessage)
    }

    if (!validateFileSize(file, this.MAX_FILE_SIZE)) {
      throw new Error(`File size ${formatFileSize(file.size)} exceeds maximum allowed size of ${formatFileSize(this.MAX_FILE_SIZE)}`)
    }

    // Additional DRM validation for media files
    if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      const mediaValidation = validateMediaFile(file)
      if (!mediaValidation.isValid) {
        throw new Error(mediaValidation.error || 'Media file validation failed')
      }
    }
  }



  /**
   * Extract metadata from media files with enhanced DRM support
   */
  private async extractMediaMetadata(file: File, metadata: ContentMetadata): Promise<void> {
    if (file.type.startsWith('image/')) {
      await this.extractImageMetadata(file, metadata)
    } else if (file.type.startsWith('video/')) {
      await this.extractVideoMetadata(file, metadata)
    } else if (file.type.startsWith('audio/')) {
      await this.extractAudioMetadata(file, metadata)
    }
  }

  /**
   * Extract image metadata (dimensions)
   */
  private async extractImageMetadata(file: File, metadata: ContentMetadata): Promise<void> {
    try {
      const dimensions = await extractMediaDimensions(file)
      if (dimensions) {
        metadata.dimensions = dimensions
      }
    } catch (error) {
      console.error('Failed to extract image metadata:', error)
    }
  }

  /**
   * Extract video metadata (duration, dimensions) with DRM support
   */
  private async extractVideoMetadata(file: File, metadata: ContentMetadata): Promise<void> {
    try {
      const [duration, dimensions] = await Promise.all([
        extractMediaDuration(file),
        extractMediaDimensions(file)
      ])
      
      metadata.duration = duration
      if (dimensions) {
        metadata.dimensions = dimensions
      }
    } catch (error) {
      console.error('Failed to extract video metadata:', error)
    }
  }

  /**
   * Extract audio metadata (duration) with DRM support
   */
  private async extractAudioMetadata(file: File, metadata: ContentMetadata): Promise<void> {
    try {
      const duration = await extractMediaDuration(file)
      metadata.duration = duration
    } catch (error) {
      console.error('Failed to extract audio metadata:', error)
    }
  }

  /**
   * Apply steganographic watermarking for DRM protection
   */
  private async applyWatermarking(file: File, cosmkId: string): Promise<File | null> {
    try {
      // For now, we'll return the original file
      // In production, this would apply invisible watermarking
      console.log('Applying DRM watermarking for COSMK ID:', cosmkId)
      
      // TODO: Implement actual steganographic watermarking
      // This would involve:
      // 1. Converting file to appropriate format
      // 2. Embedding watermark data in least significant bits
      // 3. Preserving file integrity
      // 4. Adding DRM headers and metadata
      
      return null // Return null for now, indicating no watermarking applied
    } catch (error) {
      console.error('DRM watermarking failed:', error)
      return null
    }
  }

  /**
   * Upload file to IPFS via Pinata
   */
  private async uploadToIPFS(file: File): Promise<string | undefined> {
    try {
      const jwtToken = import.meta.env.VITE_PINATA_JWT
      const apiKey = import.meta.env.VITE_PINATA_API_KEY
      
      // Prefer JWT token for authentication, fallback to API key
      const authToken = jwtToken || apiKey
      
      if (!authToken) {
        throw new Error('Pinata authentication is not configured. Please add VITE_PINATA_JWT or VITE_PINATA_API_KEY to your environment variables.')
      }

      const formData = new FormData()
      formData.append('file', file)

      // Add metadata to ensure private upload
      const metadata = {
        name: file.name,
        description: 'Private content uploaded via CosmicSource',
        private: true // Ensure file is private
      }
      formData.append('pinataMetadata', JSON.stringify(metadata))

      // Add options to ensure private upload
      const options = {
        cidVersion: 1,
        wrapWithDirectory: false,
        private: true // Ensure file is private
      }
      formData.append('pinataOptions', JSON.stringify(options))

      const response = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      })

      if (response.ok) {
        const result = await response.json()
        console.log('File uploaded to IPFS as private:', result.IpfsHash)
        return result.IpfsHash
      } else {
        const errorText = await response.text()
        console.error('IPFS upload failed:', response.status, errorText)
        
        if (response.status === 401) {
          throw new Error('Pinata authentication failed. Please check your API key in the environment variables.')
        } else if (response.status === 403) {
          throw new Error('Pinata access denied. Please check your API key permissions.')
        } else {
          throw new Error(`IPFS upload failed: ${response.status} - ${errorText}`)
        }
      }
    } catch (error) {
      console.error('IPFS upload error:', error)
      throw error
    }
  }

  /**
   * Get IPFS gateway URL for private content
   */
  private getIPFSGatewayUrl(ipfsHash: string): string {
    const customGateway = import.meta.env.VITE_PINATA_GATEWAY
    if (customGateway) {
      return `https://${customGateway}/ipfs/${ipfsHash}`
    }
    // Fallback to public gateway
    return `https://gateway.pinata.cloud/ipfs/${ipfsHash}`
  }

  /**
   * Upload content to the platform with DRM protection
   */
  async uploadContent(
    file: File, 
    options: UploadOptions, 
    onProgress?: (progress: number) => void
  ): Promise<ContentItem> {
    const maxRetries = 3
    let lastError: Error | null = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Process the file with DRM protection and compression
        const result = await this.processFile(file, options, (uploadProgress) => {
          // Convert detailed progress to simple number for backward compatibility
          onProgress?.(uploadProgress.progress)
        })

        // Check if IPFS upload was successful
        if (!result.ipfsHash) {
          throw new Error('Failed to upload file to IPFS. Please check your Pinata API credentials.')
        }

        // Try to upload to platform backend
        try {
          const response = await contentApi.uploadContent(
            result.watermarkedFile || file,
            result.metadata,
            onProgress
          )

          if (!response.success) {
            throw new Error(response.error || 'Upload failed')
          }

          // Add IPFS gateway URL if available
          if (result.ipfsHash) {
            response.data.ipfsUrl = this.getIPFSGatewayUrl(result.ipfsHash)
            response.data.ipfsHash = result.ipfsHash
          }

          // Add redirect information from API response
          const contentItem = response.data
          if (response.redirectTo) {
            contentItem.redirectTo = response.redirectTo
          }
          if (response.recordId) {
            contentItem.recordId = response.recordId
          }

          return contentItem
        } catch (backendError: any) {
          // If backend is not available, create a mock response with IPFS data
          console.warn('Backend not available, using IPFS-only upload:', backendError.message)
          
          const mockContentItem: ContentItem = {
            id: result.cosmkId, // Use COSMK ID as temporary ID
            tenantId: 'temp-tenant',
            cosmkId: result.cosmkId,
            originalFilename: file.name,
            ipfsHash: result.ipfsHash,
            ipfsUrl: this.getIPFSGatewayUrl(result.ipfsHash),
            metadata: result.metadata,
            status: 'completed',
            createdAt: new Date(),
            updatedAt: new Date()
          }

          return mockContentItem
        }
      } catch (error) {
        lastError = error as Error
        console.error(`Upload attempt ${attempt} failed:`, error)
        
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attempt) * 1000
          await new Promise(resolve => setTimeout(resolve, delay))
          console.log(`Retrying upload in ${delay}ms...`)
        }
      }
    }

    // All retries failed
    console.error('All upload attempts failed:', lastError)
    throw lastError || new Error('Upload failed after multiple attempts')
  }

  /**
   * Get upload progress
   */
  getUploadProgress(uploads: UploadProgress[]): number {
    if (uploads.length === 0) return 0
    
    const totalProgress = uploads.reduce((sum, upload) => sum + upload.progress, 0)
    return Math.round(totalProgress / uploads.length)
  }

  /**
   * Validate file for DRM compatibility
   */
  async validateFileForDRM(file: File): Promise<{ isValid: boolean; error?: string }> {
    try {
      // Basic file validation
      if (!file || file.size === 0) {
        return { isValid: false, error: 'File is empty or invalid' }
      }

      // Enhanced file type and extension validation
      const typeValidation = validateFileTypeAndExtension(file)
      
      if (!typeValidation.isValid) {
        let errorMessage = typeValidation.error || 'Invalid file format'
        if (typeValidation.suggestedCorrection) {
          errorMessage += `. ${typeValidation.suggestedCorrection}`
        }
        return { isValid: false, error: errorMessage }
      }

      // Legacy validation for additional checks
      this.validateFile(file)
      
      // Additional DRM-specific validation
      if (file.size === 0) {
        return { isValid: false, error: 'File is empty' }
      }

      // Check if file can be processed for metadata
      if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
        try {
          // Create a temporary metadata object for validation
          const tempMetadata: ContentMetadata = {
            title: file.name,
            description: '',
            tags: [],
            category: '',
            size: file.size,
            mimeType: file.type,
            fingerprint: '',
            isPublic: false
          }
          
          await this.extractMediaMetadata(file, tempMetadata)
        } catch (error) {
          console.warn('Media metadata extraction failed:', error)
          // Don't fail validation for metadata extraction errors
          // Just log the warning and continue
        }
      }

      return { isValid: true }
    } catch (error) {
      console.error('File validation error:', error)
      return { 
        isValid: false, 
        error: error instanceof Error ? error.message : 'File validation failed' 
      }
    }
  }
}

export const uploadService = new UploadService()
export default uploadService 